import {
  ChangeEvent,
  forwardRef,
  KeyboardEvent,
  memo,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import debounce from 'lodash/debounce';
import { SearchInputProps } from './search-input.types';
import { Icons } from 'assets';
import { Input } from '../input';

const SEARCH_DELAY = 250;

export const SearchInput = memo(
  forwardRef<HTMLInputElement, SearchInputProps>((props, ref) => {
    const {
      onSearch,
      onClear,
      searchOn = 'enter',
      searchFrom = 0,
      onChange,
      onKeyDown,
      ...rest
    } = props;

    /*
     * Колбэки держим в ref, чтобы debounce и ref-колбэк не пересоздавались на
     * каждый рендер: пересозданный debounce не копит вызовы, а пересозданный
     * ref-колбэк React переприсваивает, и отмена сработала бы посреди ввода.
     */
    const onSearchRef = useRef(onSearch);
    onSearchRef.current = onSearch;

    const onClearRef = useRef(onClear);
    onClearRef.current = onClear;

    const forwardedRef = useRef(ref);
    forwardedRef.current = ref;

    const debouncedSearch = useMemo(
      () =>
        debounce((value: string) => onSearchRef.current?.(value), SEARCH_DELAY),
      []
    );

    const attachRef = useCallback(
      (node: HTMLInputElement | null) => {
        if (!node) {
          debouncedSearch.cancel();
        }

        const outer = forwardedRef.current;

        if (typeof outer === 'function') {
          outer(node);
        } else if (outer) {
          outer.current = node;
        }
      },
      [debouncedSearch]
    );

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        onChange?.(event);

        if (!value) {
          // Пустое поле — это onClear, а не поиск по пустой строке
          debouncedSearch.cancel();
          onClearRef.current?.();

          return;
        }

        if (searchOn === 'type') {
          if (value.length < searchFrom) {
            debouncedSearch.cancel();
            onClearRef.current?.();

            return;
          }

          debouncedSearch(value);
        }
      },
      [debouncedSearch, onChange, searchFrom, searchOn]
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && searchOn === 'enter') {
          const { value } = event.currentTarget;

          value ? onSearchRef.current?.(value) : onClearRef.current?.();
        }

        onKeyDown?.(event);
      },
      [onKeyDown, searchOn]
    );

    return (
      <Input
        ref={attachRef}
        type="search"
        allowClear
        prefix={<Icons.Search aria-hidden />}
        placeholder="Поиск"
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        {...rest}
      />
    );
  })
);

SearchInput.displayName = 'SearchInput';
